import React, { useState } from "react";
import { toast } from "react-toastify";
import CircularProgress from "@material-ui/core/CircularProgress";
import moment from "moment-jalaali";
import Button from "../../../components/UI/Button/Button";
import Input from "../../../components/UI/Input/Input";
import fetchData from "../../../shared/fetchData";
import { checkValidity, updateObject } from "../../../shared/utility";
import IntervalEditForm, {
  PASSWORD_TYPES,
} from "../components/IntervalEditForm";
import OrderInfoDisplay from "../components/OrderInfoDisplay";
import ChangeReservationResults from "../components/ChangeReservationResults";
import {
  buildChangeReservationPayload,
  notifyChangeReservationOutcome,
  parseChangeReservationResponse,
  submitChangeOrderReservation,
} from "../utils/changeOrderReservation";
import { isEmptyTime } from "../../../components/UI/IntervalTimePicker/IntervalTimePicker";
import "../ChangeOrderInterval.css";

const API_SEARCH_ORDER = "management/api/v1/orders/searchOrdersByFilter";
const API_GET_PASSWORDS = "management/api/v1/orders/getOrderPasswords";
const emptyPasswordExpires = () => {
  const expires = {};
  PASSWORD_TYPES.forEach((type) => {
    expires[type] = { expireDate: "", expireTime: "" };
  });
  return expires;
};

const SingleOrder = () => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [passwords, setPasswords] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [passwordExpires, setPasswordExpires] = useState(emptyPasswordExpires);
  const [invalidDate, setInvalidDate] = useState(false);
  const [invalidTime, setInvalidTime] = useState(false);
  const [submitOutcome, setSubmitOutcome] = useState(null);
  const [searchForm, setSearchForm] = useState({
    courierTrackNumber: {
      elementType: "input",
      elementConfig: {
        type: "text",
        placeholder: "شناسه سفارش دهنده",
      },
      validation: {
        required: true,
      },
      errorMessage: "الزامی است",
      valid: true,
      value: "",
    },
  });

  const inputChangedHandler = (event, controlName) => {
    const updatedControls = updateObject(searchForm, {
      [controlName]: updateObject(searchForm[controlName], {
        value: event.target.value,
        valid: checkValidity(
          event.target.value,
          searchForm[controlName].validation
        ),
      }),
    });
    setSearchForm(updatedControls);
  };

  const resetEditForm = () => {
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
    setPasswordExpires(emptyPasswordExpires());
    setInvalidDate(false);
    setInvalidTime(false);
  };

  const fetchPasswords = (orderId) => {
    const raw = JSON.stringify({
      orderId: orderId,
      pl: 100,
      pn: 1,
      version: "mtpx001",
    });
    return fetchData(raw, API_GET_PASSWORDS).then((response) => response.json());
  };

  const searchOrder = () => {
    const trackNumber = searchForm.courierTrackNumber.value.trim();
    if (!trackNumber) {
      const updated = updateObject(searchForm, {
        courierTrackNumber: updateObject(searchForm.courierTrackNumber, {
          valid: false,
        }),
      });
      setSearchForm(updated);
      return;
    }

    setLoading(true);
    setOrder(null);
    setPasswords([]);
    resetEditForm();

    const today = moment();
    const raw = JSON.stringify({
      filter: {
        fromDate: moment(today).subtract(1, "jMonth").format("jYYYY/jMM/jDD"),
        toDate: moment(today).add(1, "jMonth").format("jYYYY/jMM/jDD"),
        courierTrackNumber: trackNumber,
      },
      pl: 1,
      pn: 1,
      version: "mtpx001",
    });

    fetchData(raw, API_SEARCH_ORDER)
      .then((response) => response.json())
      .then((result) => {
        if (
          result.status == 0 &&
          result.orders != null &&
          result.orders.length > 0
        ) {
          const foundOrder = result.orders[0];
          setOrder(foundOrder);
          return fetchPasswords(foundOrder.id).then((passResult) => {
            if (passResult.passwords != null) {
              setPasswords(passResult.passwords);
            }
            setLoading(false);
          });
        }
        toast.error("سفارشی با این شناسه سفارش دهنده یافت نشد");
        setLoading(false);
      })
      .catch(() => {
        toast.error("خطا در جستجوی سفارش");
        setLoading(false);
      });
  };

  const validateForm = () => {
    let valid = true;
    if (!startDate || !endDate) {
      valid = false;
      setInvalidDate(true);
    }
    if (isEmptyTime(startTime) || isEmptyTime(endTime)) {
      valid = false;
      setInvalidTime(true);
    }
    return valid;
  };

  const submitChange = () => {
    if (!order) return;
    if (!validateForm()) {
      toast.error("لطفاً فیلدهای الزامی را تکمیل کنید");
      return;
    }

    const trackNumber = order.courierTrackNumber;
    if (!trackNumber) {
      toast.error("شناسه سفارش دهنده یافت نشد");
      return;
    }

    setLoading(true);
    setSubmitOutcome(null);
    const payload = buildChangeReservationPayload({
      courierTrackNumbers: [trackNumber],
      startDate,
      startTime,
      endDate,
      endTime,
      passwordExpires,
      passwordTypes: PASSWORD_TYPES,
    });

    submitChangeOrderReservation(payload)
      .then((result) => {
        setLoading(false);
        const outcome = parseChangeReservationResponse(result);
        setSubmitOutcome(outcome);
        notifyChangeReservationOutcome(outcome, toast);
        if (outcome.kind === "success") {
          searchOrder();
        }
      })
      .catch(() => {
        setLoading(false);
        toast.error("خطا در ارتباط با سرور");
      });
  };

  const onPasswordExpireDateChange = (type, formatted) => {
    setPasswordExpires((prev) => ({
      ...prev,
      [type]: { ...prev[type], expireDate: formatted },
    }));
  };

  const onPasswordExpireTimeChange = (type, value) => {
    setPasswordExpires((prev) => ({
      ...prev,
      [type]: { ...prev[type], expireTime: value },
    }));
  };

  const formElementsArray = [];
  for (let key in searchForm) {
    formElementsArray.push({ id: key, config: searchForm[key] });
  }

  const searchInputs = formElementsArray.map((formElement) => (
    <Input
      key={formElement.id}
      elementType={formElement.config.elementType}
      elementConfig={formElement.config.elementConfig}
      value={formElement.config.value}
      invalid={!formElement.config.valid}
      shouldValidate={formElement.config.validation}
      placeholder={formElement.config.placeholder}
      errorMessage={formElement.config.errorMessage}
      col="12"
      changed={(event) => inputChangedHandler(event, formElement.id)}
    />
  ));

  return (
    <div className="change-interval-cntnr change-interval-single-tab">
      <div className="change-interval-search-row">
        <div className="change-interval-search-input-wrap">{searchInputs}</div>
        <div className="change-interval-search-btn-wrap">
          <Button btnType="Success" clicked={searchOrder}>
            جستجو
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="change-interval-loading">
          <CircularProgress className="change-interval-loading-spinner" size={56} />
        </div>
      ) : (
        ""
      )}

      {order && !loading ? (
        <div className="change-interval-section">
          <OrderInfoDisplay order={order} passwords={passwords} />
          <IntervalEditForm
            startDate={startDate}
            startTime={startTime}
            endDate={endDate}
            endTime={endTime}
            passwordExpires={passwordExpires}
            invalidDate={invalidDate}
            invalidTime={invalidTime}
            onStartDateChange={(unix, formatted) => setStartDate(formatted)}
            onStartTimeChange={setStartTime}
            onEndDateChange={(unix, formatted) => setEndDate(formatted)}
            onEndTimeChange={setEndTime}
            onPasswordExpireDateChange={onPasswordExpireDateChange}
            onPasswordExpireTimeChange={onPasswordExpireTimeChange}
            headerText="تغییر بازه رزرو و انقضای رمز"
          />
          <div className="change-interval-submit-bar">
            <div className="change-interval-submit-info">
              پس از تکمیل بازه رزرو، تغییرات را ثبت کنید
            </div>
            <Button
              btnType="Success"
              className="change-interval-submit-btn"
              clicked={submitChange}
            >
              ثبت تغییرات
            </Button>
          </div>
          <ChangeReservationResults outcome={submitOutcome} />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default SingleOrder;
